import { useCallback, useMemo } from "react";

import { Button, ButtonProps, Tooltip } from "@mui/material";
import { CropFree } from "@mui/icons-material";

import useEditVideoFile from "../../../backend/useEditVideoFile";
import { useAppContext } from "../../../hooks/useAppContext";
import useGetVideoInfo from "../../../hooks/useGetVideoInfo";
import removeAndShiftSubtitles from "../../../utils/removeAndShiftSubtitles";

// 1. Import the Autonomous Brain
import { useKernel } from "../../../hooks/AIKernel"; // Adjust relative path as needed

const VideoTrimButton: React.FC<ButtonProps> = ({ ...buttonProps }) => {
  const {
    timelineStartTime,
    timelineEndTime,
    isProcessing,
    videoRef,
    subtitles,
    setSubtitles,
  } = useAppContext();
  const { duration } = useGetVideoInfo();
  const { cutVideo } = useEditVideoFile();

  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();

  // 3. Adaptive Theme Variables
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const highlightCTA = agentState?.ui?.highlightCTAs;

  // Improved strict checking for changes
  const hasChanges = useMemo(
    () => timelineStartTime > 0 || timelineEndTime < duration,
    [duration, timelineEndTime, timelineStartTime]
  );

  const handleApplychanges = useCallback(async () => {
    // Trimming keeps the selected segment and discards the rest
    const resultBlobUrl = await cutVideo([
      [timelineStartTime, timelineEndTime],
    ]);

    if (!resultBlobUrl || !videoRef) {
      // TELEMETRY: Trim operation failed (Friction)
      if (reportBehavior) reportBehavior("video_trim_error", 2, "behavior");
      return;
    }

    if (subtitles?.length) {
      const newSubtitles = removeAndShiftSubtitles(subtitles, [
        [0, timelineStartTime],
        [timelineEndTime, duration],
      ]);
      setSubtitles(newSubtitles);
    }

    // TELEMETRY: Successful manual trim application
    if (reportBehavior) reportBehavior("video_manual_trim_applied", 8, "interest");

    videoRef.src = resultBlobUrl;
  }, [
    cutVideo,
    duration,
    setSubtitles,
    subtitles,
    timelineEndTime,
    timelineStartTime,
    videoRef,
    reportBehavior
  ]);

  return (
    <Tooltip
      arrow
      disableFocusListener
      placement="top"
      title="Trims parts of the video outside the selection"
    >
      <span>
        <Button
          {...buttonProps}
          disabled={!hasChanges || isProcessing}
          startIcon={<CropFree color="inherit" />}
          size="small"
          variant="contained"
          onClick={handleApplychanges}
          sx={{ 
            textTransform: "none",
            // ADAPTIVE UI: Pulse effect if the user is hovering in frustration and has changes ready to trim
            animation: highlightCTA && hasChanges && !isProcessing ? "pulse-trim 2s infinite" : "none",
            backgroundColor: isDarkMode ? "primary.dark" : "primary.main",
            color: "#fff",
            "&:hover": {
              backgroundColor: isDarkMode ? "primary.main" : "primary.dark",
            },
            "@keyframes pulse-trim": {
              "0%": { boxShadow: "0 0 0 0 rgba(25, 118, 210, 0.7)" },
              "70%": { boxShadow: "0 0 0 8px rgba(25, 118, 210, 0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(25, 118, 210, 0)" }
            }
          }}
        >
          Trim
        </Button>
      </span>
    </Tooltip>
  );
};

export default VideoTrimButton;